import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../lib/hooks";
import { useLang } from "../../lib/i18n";
import { PageShell } from "../../components/PublicLayout";
import { ButtonLink } from "../../components/ui";

function Prose({ eyebrow, title, updated, children }: { eyebrow: string; title: string; updated?: string; children: ReactNode }) {
  const { tx } = useLang();
  useDocumentTitle(title);
  return (
    <PageShell narrow>
      <div className="eyebrow text-saffron-600">{eyebrow}</div>
      <h1 className="mt-3 text-[clamp(2.2rem,4.5vw,3.2rem)] font-semibold tracking-tight">{title}</h1>
      {updated && <p className="mt-2 text-[13px] text-ink-400">{tx(`Last updated ${updated}`, `आख़िरी बार बदला गया: ${updated}`)}</p>}
      <div className="mt-10 space-y-6 text-[16px] leading-[1.8] text-ink-700 [&_h2]:mt-12 [&_h2]:text-[20px] [&_h2]:font-semibold [&_h2]:text-ink-900 [&_li]:ml-5 [&_li]:list-disc [&_strong]:text-ink-900">{children}</div>
    </PageShell>
  );
}

export function About() {
  const { tx, lang } = useLang();
  return (
    <Prose eyebrow={tx("About", "हमारे बारे में")} title={tx("Seva Sangrah", "सेवा संग्रह")}>
      {lang === "hi" ? (
        <>
          <p className="display text-[clamp(1.6rem,3vw,2.2rem)] leading-snug text-ink-900">असली ताक़त जनता के पास है। जनता ही यह ताक़त सौंपती है। सरकारी कर्मचारी उसका इस्तेमाल करते हैं। सेवा संग्रह याद रखता है कि क्या हुआ।</p>
          <p>
            सेवा संग्रह जनहित के लिए बना एक नागरिक मंच है। यह लोगों के अनुभवों को याद रखता है, यह दिखाता है कि किस काम की ज़िम्मेदारी किसकी है, नागरिकों की हिफ़ाज़त करता है, नेताओं के वादे दर्ज करता है, सबूत सँभालकर रखता है, मामलों को उस विभाग तक पहुँचाता है जिसके पास कार्रवाई का अधिकार है, और लिखकर रखता है कि क्या किया गया — और क्या नहीं।
          </p>
          <h2>जवाबदेही की कड़ी</h2>
          <p>
            <strong>ताक़त → ज़िम्मेदारी → नतीजा → जवाबदेही → सबक़।</strong> हर रिकॉर्ड इन सवालों का जवाब देने के लिए बना है: ताक़त किसके हाथ में थी, ज़िम्मेदारी किसकी थी, नागरिक के साथ क्या हुआ, संस्था ने क्या जवाब दिया, और इससे क्या सीखा जा सकता है।
          </p>
          <h2>हम क्या हैं — और क्या नहीं</h2>
          <p>
            सेवा संग्रह कोई सरकारी दफ़्तर, पुलिस, अनुशासन समिति या अदालत नहीं है। हम किसी संस्था को काम करने के लिए मजबूर नहीं कर सकते। हमारा काम है — याद रखना, बातों को इकट्ठा करना, दर्ज करना, बार-बार होने वाली समस्याओं को सामने लाना, मामले को सही विभाग तक पहुँचाना, और यह दर्ज करना कि जवाब आया या नहीं।
          </p>
          <h2>याद रखना, समझना, आवाज़ उठाना, आगे बढ़ाना</h2>
          <ul>
            <li>याद रखना — लोगों के अनुभव, शिकायतें, वादे, सबूत और नतीजे दर्ज करना।</li>
            <li>समझना — जमा हुए रिकॉर्ड से पता लगाना कि कौन-सी समस्याएँ बार-बार आ रही हैं।</li>
            <li>आवाज़ उठाना — ऐसी पक्की और साझा समस्याओं को सही संस्था या जनप्रतिनिधि के सामने रखना।</li>
            <li>आगे बढ़ाना — दर्ज करना कि जवाब मिला या नहीं, और मामला किस-किस दफ़्तर से होकर गुज़रा।</li>
            <li>सीखना — नतीजों को सँभालकर रखना, ताकि संस्थाएँ और नागरिक दोनों उनसे सीख सकें।</li>
          </ul>
          <h2>दफ़्तरों के चक्कर काटना नागरिक का काम नहीं</h2>
          <p>
            जब किसी नागरिक को वही काग़ज़ एक विभाग से दूसरे विभाग ले जाने पड़ते हैं, तो असल में सरकार अपने विभागों को जोड़ने का काम आम आदमी से करवा रही होती है। सेवा संग्रह ऐसी भागदौड़ को दर्ज करता है, ताकि ऐसी गड़बड़ियाँ उस स्तर पर दिखें जहाँ उन्हें ठीक किया जा सकता है — स्थानीय, ज़िला, राज्य या देश के स्तर पर।
          </p>
          <h2>राजनीति से अलग</h2>
          <p>
            सेवा संग्रह राजनीतिक सत्ता से ऊपर नहीं, उसके नीचे रहकर काम करता है। यह न सरकार चलाता है, न क़ानून बनाता है, न किसी को नियुक्त करता है, न फ़ैसले सुनाता है, न अदालत की जगह लेता है। हम लोकतंत्र को एक साँचे में नहीं ढालते; हम लोकतंत्र की याददाश्त मज़बूत करते हैं।
          </p>
          <h2>रिकॉर्ड की देखरेख</h2>
          <p>
            जनता का रिकॉर्ड ही असली और आधिकारिक रिकॉर्ड है। इसके आसपास बनी कोई भी व्यावसायिक सेवा सिर्फ़ नियंत्रित, जोड़-घटाकर बनाए गए या साफ़ तौर पर मंज़ूर किए गए आँकड़े ही इस्तेमाल कर सकती है, और कभी भी असल नागरिक डेटा की मालिक नहीं होती। लंबे समय में हमारा इरादा है कि इस रिकॉर्ड की देखरेख कोई उपयुक्त सार्वजनिक संस्था करे।
          </p>
        </>
      ) : (
        <>
          <p className="display text-[clamp(1.6rem,3vw,2.2rem)] leading-snug text-ink-900">People are sovereign. They delegate authority. Public servants exercise it. Seva Sangrah remembers what happens.</p>
          <p>
            Seva Sangrah is public-interest civic infrastructure. It preserves civic memory, makes public responsibilities observable, protects citizens, records public promises, preserves evidence, routes matters to the institutions with lawful authority, and documents what is done — and what is not.
          </p>
          <h2>The accountability chain</h2>
          <p>
            <strong>Authority → Responsibility → Outcome → Accountability → Learning.</strong> Every record is designed to answer: who held authority, who was responsible, what happened to the citizen, how the institution responded, and what can be learned.
          </p>
          <h2>What we are — and are not</h2>
          <p>
            Seva Sangrah is not a government authority, police authority, disciplinary authority or court. It cannot compel any institution to act. Its role is to remember, aggregate, document, surface patterns, route matters to the institution with lawful authority, and preserve the response or non-response.
          </p>
          <h2>Remember, understand, represent, escalate</h2>
          <ul>
            <li>Remember — capture citizen experiences, complaints, promises, evidence and outcomes.</li>
            <li>Understand — identify patterns and recurring issues from accumulated records.</li>
            <li>Represent — make credible collective patterns visible to the appropriate institution or representative.</li>
            <li>Escalate — record responses, non-responses and movement through institutional channels.</li>
            <li>Learn — preserve outcomes so institutions and citizens can learn from what happened.</li>
          </ul>
          <h2>The citizen should not be the API</h2>
          <p>
            When a citizen must carry the same documents from one department to another, the State is using the citizen as its integration layer. Seva Sangrah preserves those journeys so that system-design failures can be seen at the level where they can be fixed — local, district, state or national.
          </p>
          <h2>Sovereignty</h2>
          <p>
            Seva Sangrah stays below the level of political sovereignty. It does not govern, legislate, appoint, adjudicate or replace courts. We don’t standardise democracy; we standardise the ability of democracy to remember.
          </p>
          <h2>Stewardship</h2>
          <p>
            The civic record is the canonical system of record. Any commercial services built around it consume only governed, aggregated or explicitly authorised views and never own the underlying civic data. The long-term intention is that the record could be stewarded by an appropriate public institution.
          </p>
        </>
      )}
      <div className="flex flex-wrap gap-3 pt-6">
        <ButtonLink to="/complaint" variant="saffron">
          {tx("Register a complaint", "शिकायत दर्ज करें")}
        </ButtonLink>
        <ButtonLink to="/join" variant="secondary">
          {tx("Join Seva Sangrah", "सेवा संग्रह से जुड़िए")}
        </ButtonLink>
      </div>
    </Prose>
  );
}

export function Standards() {
  const { tx, lang } = useLang();
  return (
    <Prose eyebrow={tx("Record standards", "रिकॉर्ड के नियम")} title={tx("How the record treats truth, evidence and identity", "रिकॉर्ड में सच, सबूत और पहचान को कैसे सँभाला जाता है")}>
      {lang === "hi" ? (
        <>
          <h2>छह चीज़ें जिन्हें हम अलग-अलग रखते हैं</h2>
          <ul>
            <li><strong>आरोप</strong> — नागरिक ने जो बताया। जैसा कहा वैसा दर्ज होता है, पर कभी सबूत की तरह पेश नहीं किया जाता।</li>
            <li><strong>जमा किए गए सबूत</strong> — फ़ाइलें बिल्कुल वैसी ही जैसी मिलीं, आते ही उनकी डिजिटल पहचान (SHA-256) दर्ज की जाती है।</li>
            <li><strong>जाँच का फ़ैसला</strong> — सेवा संग्रह की टीम का सबूतों पर आकलन। यह कोई सरकारी फ़ैसला नहीं है।</li>
            <li><strong>जाँच का नतीजा</strong> — सिर्फ़ वही जाँच एजेंसी दर्ज कर सकती है जिसे उस मामले की साफ़ इजाज़त मिली हो।</li>
            <li><strong>सरकारी जवाब</strong> — ज़िम्मेदार संस्था या जनप्रतिनिधि ने क्या कहा या क्या किया।</li>
            <li><strong>आख़िरी नतीजा</strong> — हर नतीजा दर्ज होता है, चाहे कोई कार्रवाई न हुई हो, शिकायत वापस ली गई हो, या सबूत काफ़ी न हों।</li>
          </ul>
          <h2>क्या छपता है</h2>
          <p>शिकायतें शुरू से निजी रहती हैं। हमारी टीम चाहे तो बिना किसी निजी जानकारी के उसका छोटा सार छाप सकती है। किसी कर्मचारी के ख़िलाफ़ आरोप तभी छपते हैं जब उनकी जाँच हो चुकी हो, और तब भी उन पर साफ़ लिखा होता है कि यह आरोप है।</p>
          <h2>पहचान</h2>
          <p>
            शिकायत करने वाले की पहचान एक अलग, सुरक्षित रिकॉर्ड में रखी जाती है। नेता, पार्टियाँ, वॉलंटियर और साथी संस्थाएँ इसे कभी नहीं देख सकते। अगर हमारी टीम के किसी सदस्य को शिकायत करने वाले से संपर्क करना हो, तो उसे वजह बताकर इजाज़त माँगनी होती है; कोई दूसरा अधिकृत व्यक्ति ही यह इजाज़त दे सकता है; इजाज़त एक तय समय बाद ख़त्म हो जाती है; और हर बार देखने का रिकॉर्ड हमेशा के लिए दर्ज रहता है।
          </p>
          <h2>मिलती-जुलती शिकायतें एक साथ</h2>
          <p>
            जब अलग-अलग लोगों की शिकायतें एक ही दफ़्तर, व्यक्ति या तरीक़े के बारे में लगती हैं, तो सिस्टम इसका इशारा कर सकता है। ऐसा पैटर्न किसी को दोषी साबित नहीं करता। किसी अधिकारी को भेजने से पहले एक इंसान — जो इसे सुझाने वाले से अलग हो — इसकी जाँच करता है। हर शिकायत की अपनी अलग पहचान बनी रहती है।
          </p>
          <h2>डेमो वाला डेटा</h2>
          <p>डेमो साइट पर सिर्फ़ काल्पनिक लोग, जगहें, पार्टियाँ और रिकॉर्ड होते हैं, और उन पर साफ़ लिखा होता है। डेमो में असली डेटा कभी नहीं होता, और असली साइट पर कभी नक़ली रिकॉर्ड नहीं होते।</p>
        </>
      ) : (
        <>
          <h2>Six things we keep separate</h2>
          <ul>
            <li><strong>Allegation</strong> — what a citizen reports. Recorded faithfully, never presented as proof.</li>
            <li><strong>Submitted evidence</strong> — files exactly as received, fingerprinted with SHA-256 on arrival.</li>
            <li><strong>Verification decision</strong> — a Seva Sangrah reviewer's assessment of the evidence. Not an official finding.</li>
            <li><strong>Investigation finding</strong> — recorded only by an investigating authority holding an explicit grant for the case.</li>
            <li><strong>Official response</strong> — what the responsible institution or representative said or did.</li>
            <li><strong>Final outcome</strong> — every outcome is recorded, including no action, withdrawal and insufficient evidence.</li>
          </ul>
          <h2>Publication</h2>
          <p>Complaints are private by default. A reviewer may publish a redacted summary with no personal details. Conduct allegations are only published after a verification decision, and always labelled as allegations.</p>
          <h2>Identity</h2>
          <p>
            Complainant identity is stored in a separate protected record. Representatives, parties, volunteers and partner organisations never see it. A reviewer who needs to contact a complainant must request access with a stated reason; a different authorised person must approve it; access expires; and every view is permanently logged.
          </p>
          <h2>Collective dockets</h2>
          <p>
            When independently submitted complaints appear to concern the same office, person or practice, the system may suggest a pattern. A pattern does not establish guilt. A human reviewer — different from the person who proposed it — must validate a docket before it is submitted to a competent authority. Each complaint remains individually attributable.
          </p>
          <h2>Demonstration data</h2>
          <p>The demonstration site uses only fictional people, places, parties and records, clearly labelled. It never uses production data, and production never contains synthetic records.</p>
        </>
      )}
    </Prose>
  );
}

export function Privacy() {
  const { tx, lang } = useLang();
  return (
    <Prose eyebrow={tx("Privacy", "प्राइवेसी")} title={tx("Privacy notice", "आपकी जानकारी की सुरक्षा")} updated={tx("October 2026", "अक्टूबर 2026")}>
      {lang === "hi" ? (
        <>
          <p>यहाँ बताया गया है कि सेवा संग्रह आपसे क्या जानकारी लेता है, क्यों लेता है, और उसे कैसे सुरक्षित रखता है। बड़े पैमाने पर शुरू करने से पहले किसी योग्य वकील से इसे डिजिटल पर्सनल डेटा प्रोटेक्शन एक्ट, 2023 के हिसाब से जँचवाया जाना चाहिए।</p>
          <h2>हम क्या जानकारी लेते हैं</h2>
          <ul>
            <li>जिस सेवा से आप साइन इन करते हैं (जैसे Google), उससे आपका नाम, ईमेल और/या फ़ोन, और एक पहचान नंबर।</li>
            <li>आप अपनी मर्ज़ी से जो जगह बताएँ (पिन कोड, ज़िला, चुनाव क्षेत्र)।</li>
            <li>आपकी शिकायतें, वादे, सबूत की फ़ाइलें, और आपकी भागीदारी (वोट, रेटिंग, समर्थन)।</li>
            <li>गोपनीय शिकायतों के लिए आप जो संपर्क जानकारी देना चाहें — यह शिकायत से अलग रखी जाती है।</li>
            <li>सुरक्षा से जुड़ी जानकारी: आपने कब साइन इन किया, आपका IP पता, और ख़ास अधिकार वाले कामों का रिकॉर्ड।</li>
          </ul>
          <h2>इसका इस्तेमाल कैसे होता है</h2>
          <p>नागरिक मामलों को दर्ज करने और सही जगह पहुँचाने के लिए, आपकी मंज़ूरी हो तो आपसे संपर्क करने के लिए, मंच को ग़लत इस्तेमाल से बचाने के लिए, और ऐसे कुल आँकड़े बनाने के लिए जिनमें किसी एक इंसान की पहचान न हो सके। हम आपकी निजी जानकारी बेचते नहीं हैं और विज्ञापनों के लिए इस्तेमाल नहीं करते।</p>
          <h2>कौन क्या देख सकता है</h2>
          <p>कौन क्या देख सकता है, यह उसकी भूमिका, इलाक़े, मामले, संवेदनशीलता और समय के हिसाब से तय होता है, और यह नियम सर्वर और डेटाबेस दोनों में लागू होता है। शिकायत करने वाले की पहचान और संवेदनशील सबूत देखने के लिए साफ़, सीमित समय वाली और दर्ज की गई इजाज़त ज़रूरी है।</p>
          <h2>गुमनाम शिकायतें</h2>
          <p>अगर आप पूरी तरह गुमनाम रहना चुनते हैं, तो आपकी कोई पहचान दर्ज नहीं होती। सिर्फ़ आप, अपने निजी ट्रैकिंग कोड से, शिकायत का हाल देख सकते हैं।</p>
          <h2>जानकारी कितने समय तक रखी जाती है</h2>
          <p>नागरिक रिकॉर्ड जनहित की याददाश्त का हिस्सा हैं, इसलिए वे अपने-आप नहीं मिटते, बल्कि समय-समय पर उनकी समीक्षा होती है। ख़त्म हो चुके लॉगिन सेशन और भेजे जा चुके ईमेल जैसी कामकाजी जानकारी तय समय पर हटा दी जाती है।</p>
          <h2>आपके पास क्या विकल्प हैं</h2>
          <p>आप कभी भी अपनी प्रोफ़ाइल बदल सकते हैं, सूचनाओं की सेटिंग बदल सकते हैं, और अपने वोट व रेटिंग वापस ले सकते हैं। अपनी निजी जानकारी देखने या उसमें सुधार करवाने के लिए <Link to="/connect" className="underline">हमसे संपर्क करें</Link>।</p>
        </>
      ) : (
        <>
          <p>This notice explains what Seva Sangrah collects, why, and how it is protected. It should be reviewed by qualified counsel against the Digital Personal Data Protection Act, 2023 before large-scale launch.</p>
          <h2>What we collect</h2>
          <ul>
            <li>Account details from your sign-in provider: name, email and/or phone, and a provider identifier.</li>
            <li>Profile location you choose to give (PIN code, district, constituency).</li>
            <li>Complaints, promises, evidence files and participation (votes, ratings, supports) you submit.</li>
            <li>For confidential complaints, the contact details you choose to give, stored separately from the complaint.</li>
            <li>Security records: sign-in times, IP address, and an audit trail of privileged actions.</li>
          </ul>
          <h2>How it is used</h2>
          <p>To record and route civic matters, to contact you if you agreed, to protect the platform from abuse, and to produce aggregated statistics in which no individual can be identified. We do not sell personal data and do not use it for advertising.</p>
          <h2>Who can see what</h2>
          <p>Access is decided by role, geography, case, sensitivity and time, and is enforced on the server and in the database. Complainant identity and sensitive evidence require an explicit, time-limited, audited grant.</p>
          <h2>Anonymous complaints</h2>
          <p>If you choose full anonymity, no identity is stored. Only you, with the private tracking code, can follow the complaint.</p>
          <h2>Retention</h2>
          <p>Civic records are kept as part of the public-interest memory and are reviewed, not automatically deleted. Operational data such as expired sessions and delivered email logs are removed on a schedule.</p>
          <h2>Your choices</h2>
          <p>You can update your profile, change notification preferences, and withdraw votes and ratings at any time. To request access to or correction of your personal data, use <Link to="/connect" className="underline">Connect with us</Link>.</p>
        </>
      )}
    </Prose>
  );
}

export function Terms() {
  const { tx, lang } = useLang();
  return (
    <Prose eyebrow={tx("Terms", "शर्तें")} title={tx("Terms of use", "इस्तेमाल की शर्तें")} updated={tx("October 2026", "अक्टूबर 2026")}>
      {lang === "hi" ? (
        <>
          <p>बड़े पैमाने पर शुरू करने से पहले इन शर्तों को किसी योग्य भारतीय वकील से जँचवाया जाना चाहिए।</p>
          <h2>सच बताइए</h2>
          <p>सिर्फ़ वही बताइए जिसे आप सच मानते हैं। जान-बूझकर झूठी शिकायत करना, नक़ली सबूत देना या किसी और का रूप धरना मना है, और इसके क़ानूनी नतीजे हो सकते हैं।</p>
          <h2>हम क़ानूनी फ़ैसला नहीं सुनाते</h2>
          <p>सेवा संग्रह यह तय नहीं करता कि कोई दोषी है या ज़िम्मेदार। हमारी जाँच के फ़ैसले सरकारी फ़ैसले नहीं हैं। इस मंच पर कुछ भी क़ानूनी सलाह नहीं है।</p>
          <h2>इज़्ज़त से भागीदारी</h2>
          <p>दूसरों की निजी जानकारी, धमकियाँ, नफ़रत भरी बातें या किसी पार्टी का प्रचार यहाँ मत डालिए। इन नियमों को तोड़ने वाली चीज़ें मॉडरेटर छिपा सकते हैं; ऐसी हर कार्रवाई दर्ज होती है।</p>
          <h2>संस्थाओं के लिए</h2>
          <p>जनप्रतिनिधि, उनके सहयोगी, पार्टियाँ, साथी संस्थाएँ, ऑडिटर और जाँच एजेंसियाँ सिर्फ़ उतना ही कर सकती हैं जितनी उन्हें इजाज़त मिली है। हर काम का रिकॉर्ड रहता है कि किसने किया। ग़लत इस्तेमाल करने पर खाता बंद कर दिया जाता है।</p>
          <h2>सेवा की उपलब्धता</h2>
          <p>हम पूरी कोशिश करते हैं कि सेवा चालू रहे और रिकॉर्ड बैकअप के साथ सुरक्षित रहे, पर बिना रुकावट सेवा की गारंटी नहीं दे सकते।</p>
        </>
      ) : (
        <>
          <p>These terms should be reviewed by qualified Indian counsel before large-scale launch.</p>
          <h2>Truthful reporting</h2>
          <p>Submit only what you believe to be true. Knowingly false complaints, fabricated evidence and impersonation are prohibited and may have legal consequences.</p>
          <h2>No legal determination</h2>
          <p>Seva Sangrah does not determine guilt or liability. Verification decisions are not official findings. Nothing on the platform is legal advice.</p>
          <h2>Respectful participation</h2>
          <p>Do not publish personal details of others, threats, hate speech or partisan campaigning. Moderators may hide participation that breaks these rules; the action is recorded.</p>
          <h2>Institutional users</h2>
          <p>Representatives, associates, parties, partner organisations, auditors and investigating authorities act only within their granted scope. Every action is attributable and audited. Misuse leads to suspension.</p>
          <h2>Availability</h2>
          <p>We work to keep the service available and the record durable, with backups, but cannot guarantee uninterrupted service.</p>
        </>
      )}
    </Prose>
  );
}

export function NotFound() {
  const { tx } = useLang();
  useDocumentTitle(tx("Not found", "पेज नहीं मिला"));
  return (
    <PageShell narrow>
      <div className="py-20 text-center">
        <div className="display text-[96px] leading-none text-ink-200">404</div>
        <h1 className="mt-4 text-[24px] font-semibold">{tx("This page is not on the record.", "यह पेज हमारे रिकॉर्ड में नहीं है।")}</h1>
        <ButtonLink to="/" className="mt-8">
          {tx("Return home", "होम पेज पर जाएँ")}
        </ButtonLink>
      </div>
    </PageShell>
  );
}
